/**
 * The browser side of the wire: a `Transport` (../net/protocol) over a
 * `WebSocket`.
 *
 * The counterpart to server/ws-transport.ts, and deliberately the only file in
 * the client tree that names `WebSocket` at all. Everything above it — the
 * `NetClient`, the lobby, the predicted session — sees the five members of
 * `Transport` and nothing else, which is the seam protocol.ts was written for:
 * a WebRTC DataChannel is a new implementation of this file and no change
 * anywhere above it.
 *
 * ===========================================================================
 * IMPORTING THIS IN NODE MUST NOT THROW
 * ===========================================================================
 *
 * The whole netcode suite runs under Node with no DOM, so nothing here touches
 * `WebSocket` at module scope and the constructor is INJECTED (`options.factory`)
 * rather than reached for globally. A test hands in a fake socket; the browser
 * gets `globalThis.WebSocket`; a Node 22+ process would get its built-in one.
 * `openSocket` is the single place that decision is made, and it throws a
 * message a human can act on rather than a `ReferenceError` when there is none.
 *
 * ===========================================================================
 * RTT IS NOT MEASURED HERE
 * ===========================================================================
 *
 * The server measures round trips with RFC 6455 ping/pong frames (see
 * server/ws-transport.ts). A browser answers those inside the WebSocket
 * implementation and gives page JavaScript NO way to observe them — there is no
 * `onping`, and `WebSocket` exposes no timing. So `rttMs` on this class is a
 * value somebody else WRITES: `NetClient` owns the estimate (from the server's
 * own measurement, echoed back in `RoomState`, and from its own input-ack
 * timing) and pushes it here through `setRtt`, so that anything reading the
 * `Transport` interface sees one number rather than two disagreeing ones.
 *
 * ===========================================================================
 * A CONNECTION THAT NEVER OPENS STILL CLOSES
 * ===========================================================================
 *
 * `WebSocket` reports a refused connection as an `error` event followed by a
 * `close` — and, on some browsers, as a `close` alone. Both paths land on
 * `finish()`, which fires the close handler exactly once with a reason string,
 * because the failure a player actually experiences ("the server is not
 * running") must reach the UI through the same channel as "the server went
 * away". A transport that only reports one of those strands the connecting
 * screen forever, which is the single most common shape of this bug.
 */

import type { Transport } from './protocol';

// ---------------------------------------------------------------------------
// The slice of WebSocket we use
// ---------------------------------------------------------------------------

/**
 * Structurally what this file needs from a `WebSocket`. Declared rather than
 * imported from lib.dom so a test can hand in a literal and so this module
 * type-checks in a build with no DOM lib.
 */
export interface SocketLike {
  binaryType: string;
  readyState: number;
  send(data: ArrayBuffer): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: 'open', handler: () => void): void;
  addEventListener(type: 'message', handler: (event: { data: unknown }) => void): void;
  addEventListener(type: 'error', handler: () => void): void;
  addEventListener(type: 'close', handler: (event: { code: number; reason: string }) => void): void;
}

export type SocketFactory = (url: string) => SocketLike;

/** `WebSocket.OPEN`. Spelled out because `WebSocket` may not exist here. */
export const SOCKET_OPEN = 1;

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

/** The path server/config.ts binds by default. A bare host:port gets this. */
export const DEFAULT_SERVER_PATH = '/play';

/**
 * Turn whatever the player typed into a WebSocket URL, or return null.
 *
 * Accepted, because all four are things people actually type:
 *
 *   ws://host:1234/play      used as-is
 *   wss://host/play          used as-is
 *   host:1234                scheme and path filled in
 *   http(s)://host/play      scheme swapped for its socket equivalent
 *
 * The scheme is upgraded to `wss` on an https page, always. A browser refuses
 * an insecure socket from a secure page, and it refuses it with a console error
 * the player will never see — so silently connecting to `ws://` from `https://`
 * produces a game that "just does not connect" with nothing on screen to say
 * why. Returning a `wss` URL means the failure, if there is one, is a real
 * connection failure the UI can report.
 */
export const normalizeServerUrl = (address: string, secure = false): string | null => {
  const raw = address.trim();
  if (raw.length === 0 || raw.length > 300) return null;

  // The scheme is matched EXPLICITLY rather than "does it already have one",
  // because prepending `ws://` to an address that has a scheme this function
  // does not know produces a URL that parses perfectly and means nothing:
  // `ftp://host/play` becomes `ws://ftp//host/play`, whose host is `ftp`. An
  // unknown scheme is a typo, and a typo must be refused rather than reinterpreted.
  let text = raw;
  const scheme = /^([a-z][a-z0-9+.-]*):\/\//i.exec(text);
  if (scheme === null) {
    text = `${secure ? 'wss' : 'ws'}://${text}`;
  } else {
    const rest = text.slice(scheme[0].length);
    switch (scheme[1].toLowerCase()) {
      case 'https':
        text = `wss://${rest}`;
        break;
      case 'http':
        text = `ws://${rest}`;
        break;
      case 'ws':
      case 'wss':
        break;
      default:
        return null;
    }
  }

  // `URL` is in every environment this runs in — browsers, Node, workers — and
  // is the only parser that agrees with what the socket will actually do.
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== 'ws:' && url.protocol !== 'wss:') return null;
  if (url.hostname.length === 0) return null;
  if (secure && url.protocol === 'ws:') url.protocol = 'wss:';
  if (url.pathname === '/' || url.pathname === '') url.pathname = DEFAULT_SERVER_PATH;
  // A query or a fragment on a socket URL is never what was meant and is the
  // shape a pasted browser address arrives in.
  url.search = '';
  url.hash = '';
  return url.toString();
};

/** True while the page is on https, so a plain `ws://` would be blocked. */
export const pageIsSecure = (): boolean => {
  try {
    return typeof location !== 'undefined' && location.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * `VITE_SERVER_URL` from the build, or an empty string.
 *
 * Read through a cast because the project does not pull in `vite/client`'s
 * types, and defensively because `import.meta.env` is a bundler substitution
 * that simply is not there in a plain Node import of this module.
 */
const configuredServerUrl = (): string => {
  try {
    const env = (import.meta as { env?: Record<string, string | undefined> }).env;
    return env?.VITE_SERVER_URL?.trim() ?? '';
  } catch {
    return '';
  }
};

/**
 * The address a fresh install should try first.
 *
 * The dev server and the game server are two processes on one machine during
 * development, so "the host you loaded the page from, on the game server's
 * port" is right far more often than any hard-coded host. Falls back to the
 * documented local default when there is no page to ask.
 *
 * A deployment behind a reverse proxy breaks that guess: there the socket is on
 * 443 under a path, not on a port of its own, and the port this would pick is
 * firewalled. `VITE_SERVER_URL` is baked in at build time to say so — CI sets
 * it to `wss://<site>/play` — and a dev build has none.
 */
export const defaultServerAddress = (port = 8080): string => {
  const configured = configuredServerUrl();
  if (configured.length > 0) return configured;
  try {
    if (typeof location !== 'undefined' && location.hostname.length > 0) {
      const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
      return `${scheme}://${location.hostname}:${port}${DEFAULT_SERVER_PATH}`;
    }
  } catch {
    /* no page — fall through */
  }
  return `ws://localhost:${port}${DEFAULT_SERVER_PATH}`;
};

// ---------------------------------------------------------------------------
// The transport
// ---------------------------------------------------------------------------

export interface WebSocketTransportOptions {
  /**
   * Builds the socket. Omit it for `globalThis.WebSocket`; pass `null` to say
   * there is none, which is what a locked-down embedding amounts to and is a
   * case worth being able to state rather than only to stumble into.
   */
  factory?: SocketFactory | null;
  /** Called once the socket is open. */
  onOpen?: () => void;
}

/**
 * A `Transport` that buffers until the socket opens.
 *
 * The buffer exists for exactly one message — the Hello, which `NetClient`
 * sends the instant it is constructed — and is capped so a socket that never
 * opens cannot accumulate a match's worth of input packets. Past the cap the
 * OLDEST are dropped, because on this protocol every stale input has already
 * been superseded by the redundancy in the next packet.
 */
export class WebSocketTransport implements Transport {
  /** Written by `NetClient`; see the header on why it is not measured here. */
  rttMs = 0;

  private socket: SocketLike | null = null;
  private open = false;
  private done = false;
  private messageHandler: ((data: ArrayBuffer) => void) | null = null;
  private closeHandler: ((reason: string) => void) | null = null;
  private readonly pending: ArrayBuffer[] = [];
  /** Frames queued before the socket opened. A handshake is one frame. */
  private static readonly PENDING_LIMIT = 8;

  constructor(readonly url: string, options: WebSocketTransportOptions = {}) {
    const factory = options.factory === undefined ? globalSocketFactory() : options.factory;
    if (factory === null) {
      // Not an exception: a missing WebSocket is a real deployment condition
      // (an old Node, a locked-down embedding) and the UI must show it as a
      // connection failure rather than as a crash on the boot path.
      queueMicrotask(() => this.finish('no WebSocket in this environment'));
      return;
    }

    let socket: SocketLike;
    try {
      socket = factory(url);
    } catch (error) {
      queueMicrotask(() => this.finish(messageOf(error)));
      return;
    }
    this.socket = socket;
    // Without this every inbound frame arrives as a Blob and has to be read
    // asynchronously, which reorders snapshots relative to control messages.
    socket.binaryType = 'arraybuffer';

    socket.addEventListener('open', () => {
      this.open = true;
      for (const frame of this.pending) this.rawSend(frame);
      this.pending.length = 0;
      options.onOpen?.();
    });
    socket.addEventListener('message', event => {
      const data = event.data;
      if (!(data instanceof ArrayBuffer)) return;
      this.messageHandler?.(data);
    });
    // An `error` on a WebSocket carries no detail by design (the spec hides it
    // to avoid leaking cross-origin information), so there is nothing to report
    // but the fact. `close` follows in every browser, and `finish` is idempotent.
    socket.addEventListener('error', () => this.finish('connection failed'));
    socket.addEventListener('close', event =>
      this.finish(event.reason.length > 0 ? event.reason : closeReason(event.code))
    );
  }

  /** True once the socket has opened and before it has closed. */
  get isOpen(): boolean {
    return this.open && !this.done;
  }

  get closed(): boolean {
    return this.done;
  }

  /** Set the round trip the rest of the system reads off this transport. */
  setRtt(ms: number): void {
    if (Number.isFinite(ms) && ms >= 0) this.rttMs = ms;
  }

  send(data: ArrayBuffer): void {
    if (this.done) return;
    if (!this.open) {
      this.pending.push(data);
      while (this.pending.length > WebSocketTransport.PENDING_LIMIT) this.pending.shift();
      return;
    }
    this.rawSend(data);
  }

  private rawSend(data: ArrayBuffer): void {
    const socket = this.socket;
    if (socket === null || socket.readyState !== SOCKET_OPEN) return;
    try {
      socket.send(data);
    } catch (error) {
      this.finish(messageOf(error));
    }
  }

  onMessage(handler: (data: ArrayBuffer) => void): void {
    this.messageHandler = handler;
  }

  onClose(handler: (reason: string) => void): void {
    this.closeHandler = handler;
    // A transport that already finished still has to tell a handler attached
    // afterwards. `NetClient` wires its handlers one statement after the
    // constructor, and a synchronous failure in between would otherwise be lost.
    if (this.done) handler(this.reason);
  }

  close(reason = ''): void {
    if (this.done) return;
    const socket = this.socket;
    if (socket !== null) {
      try {
        socket.close(1000, reason.slice(0, 120));
      } catch {
        /* already gone */
      }
    }
    this.finish(reason.length > 0 ? reason : 'closed');
  }

  private reason = '';

  private finish(reason: string): void {
    if (this.done) return;
    this.done = true;
    this.open = false;
    this.reason = reason;
    this.pending.length = 0;
    const handler = this.closeHandler;
    this.closeHandler = null;
    this.messageHandler = null;
    handler?.(reason);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * `globalThis.WebSocket`, or null.
 *
 * Read through `globalThis` and behind a try, because a page with a strict CSP
 * or a sandboxed iframe can make even the property access throw.
 */
export const globalSocketFactory = (): SocketFactory | null => {
  try {
    const ctor = (globalThis as { WebSocket?: new (url: string) => SocketLike }).WebSocket;
    if (typeof ctor !== 'function') return null;
    return (url: string) => new ctor(url);
  } catch {
    return null;
  }
};

/**
 * A close code as something a player can read.
 *
 * Only the codes this protocol can actually produce are named. 1006 is the one
 * that matters: it is what a browser reports for a connection that was never
 * established, i.e. "the server is not running", and reporting it as "code
 * 1006" instead of that is the difference between a bug report and a fixed
 * problem.
 */
export const closeReason = (code: number): string => {
  switch (code) {
    case 1000:
      return 'closed';
    case 1001:
      return 'server going away';
    case 1006:
      return 'could not reach the server';
    case 1009:
      return 'frame too large';
    case 1011:
      return 'server error';
    case 1015:
      return 'TLS handshake failed';
    default:
      return `closed (code ${code})`;
  }
};
